import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { atom, useAtom } from 'jotai';
import { isEmpty } from 'lodash';
import { type EnqueueSnackbar, useSnackbar } from 'notistack';
import { useState } from 'react';

import { useFetchOrganizations } from '@/pages/Organizations';
import {
  axiosClient,
  getResponseErrorMessages
} from '@/services/BaseApi.service';

import type {
  OrganizationCreateRequest,
  OrganizationEditDialogForm,
  OrganizationEditDialogMode
} from './OrganizationEditDialog.types';

export const EMPTY_ORGANIZATION: OrganizationCreateRequest = {
  id: -1,
  name: '',
  label: '',
  is_ir: false,
  is_auto_soc: 0,
  is_restricted: false
};

export const organizationEditDialogOrgAtom =
  atom<OrganizationCreateRequest>(EMPTY_ORGANIZATION);
const organizationEditDialogOpenAtom = atom(false);
const organizationEditDialogModeAtom = atom<OrganizationEditDialogMode>('New');
const organizationEditDialogRepoNamesAtom = atom<string[]>([]);

const saveNewOrganization = (org: OrganizationCreateRequest) =>
  axiosClient.post('/organizations/', {
    name: org.name.trim(),
    label: org.label.trim().toLowerCase(),
    uci: org.uci,
    is_ir: !!org.is_ir
  });

const saveDemoOrganization = (org: OrganizationCreateRequest) =>
  axiosClient.post('/organizations/demo', {
    name: org.name.trim(),
    label: org.label.trim(),
    demo: true
  });

const updateOrganization = (org: OrganizationCreateRequest) =>
  axiosClient.put(`/organizations/${org.id}`, {
    name: org.name.trim(),
    label: org.label.trim()
  });

const deleteOrganization = (id: number) =>
  axiosClient.delete(`/organizations/${id}`);

const salesforceSyncOrganization = () =>
  axiosClient.post('/organizations/salesforce_sync');

const cleanUpOrganization = (id: number, reposToRemove: string[]) =>
  axiosClient.post(`/organizations/${id}/clean_up`, {
    repos_to_remove: reposToRemove
  });

const showSnackbar = (
  enqueueSnackbar: EnqueueSnackbar,
  mode: OrganizationEditDialogMode
) => {
  let action = '';
  switch (mode) {
    case 'Edit':
      action = 'Updated';
      break;
    case 'Delete':
      action = 'Deleted';
      break;
    case 'Demo':
      action = 'Demo Created';
      break;
    case 'SalesforceSync':
      action = 'Sync in progress, please wait a few minutes';
      break;
    case 'CleanUp':
      action = 'Clean Up in progress, please wait a few minutes';
      break;
    default:
      action = 'Saved';
  }
  enqueueSnackbar(`Organization ${action}`, {
    variant: 'success'
  });
};

export const useOrganizationDialog = () => {
  const [errorState, setErrorState] = useState<{
    message: string;
    organization: OrganizationCreateRequest;
  } | null>(null);
  const [mode, setMode] = useAtom(organizationEditDialogModeAtom);
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useAtom(organizationEditDialogOpenAtom);
  const { refetch } = useFetchOrganizations();
  const [organization, setOrganization] = useAtom(
    organizationEditDialogOrgAtom
  );
  const [repoNames, setRepoNames] = useAtom(
    organizationEditDialogRepoNamesAtom
  );

  // Errors belong to the organization they were raised for; a changed
  // organization hides the stale error.
  const error =
    errorState?.organization === organization ? errorState.message : '';
  const setError = (message: string) =>
    setErrorState({ message, organization });

  const isSyncAllowed = () => {
    const savedTime = Number(localStorage.getItem('lastSalesforceSyncTime'));
    if (!savedTime) return true;
    const timeToWait = 5 * 60 * 1000; // time to wait in milliseconds
    const syncAllowed = Date.now() - savedTime > timeToWait;
    return syncAllowed;
  };

  const triggerSync = () => {
    const currentTime = Date.now();
    localStorage.setItem('lastSalesforceSyncTime', currentTime.toString());
    return currentTime;
  };

  const openDialog = (
    dialogMode: OrganizationEditDialogMode = 'New',
    org: OrganizationCreateRequest = EMPTY_ORGANIZATION
  ) => {
    setErrorState(null);
    setMode(dialogMode);
    setOrganization(org);

    if (dialogMode === 'SalesforceSync') {
      if (isSyncAllowed()) {
        setOpen(true);
      }
    } else {
      setOpen(true);
    }
  };

  const closeDialog = () => {
    setErrorState(null);
    setOrganization(EMPTY_ORGANIZATION);
    setRepoNames([]);
    setOpen(false);
  };

  const mutation = useMutation({
    mutationFn: (org: OrganizationEditDialogForm) => {
      switch (mode) {
        case 'Edit':
          return updateOrganization(org);
        case 'Demo':
          return saveDemoOrganization(org);
        case 'Delete':
          return deleteOrganization(org.id);
        case 'SalesforceSync':
          return salesforceSyncOrganization();
        case 'CleanUp':
          return cleanUpOrganization(org.id, repoNames);
        default: {
          const newOrg = { ...org };
          if (isEmpty(org.label) || !isEmpty(org.newLabel?.trim())) {
            newOrg.label = org.newLabel!;
          }
          return saveNewOrganization(newOrg);
        }
      }
    },
    onSuccess: () => {
      if (mode === 'SalesforceSync') {
        triggerSync();
      }

      closeDialog();
      showSnackbar(enqueueSnackbar, mode);
      refetch();
    },
    onError: (err) => {
      if (mode === 'Delete') {
        setError(getResponseErrorMessages(err as AxiosError));
      } else {
        setError('Error executing operation');
      }
    }
  });

  return {
    organization,
    repoNames,
    open,
    error,
    mutation,
    mode,
    setError,
    openDialog,
    closeDialog,
    setOrganization,
    setRepoNames,
    isSyncAllowed,
    triggerSync
  };
};
